import React, {Component} from 'react';
import {connect} from 'react-redux';
import PropTypes from 'prop-types';
import {bindActionCreators} from "redux";

import {Button, Card, Col, Form, Input, InputNumber, notification, Row, Select} from 'antd';

import {receiveProduct, getProduct, getProductStocks, generateBarcode} from './actions';
import {getDescriptionFromError} from "../utils/errors";
import {getPurchaseOrder} from "../PurchaseOrder/actions";
import {formItemLayout} from "../utils/style";
import StockDisplay from "./StockDisplay";
import {decodeParams} from "../utils/params";

const FormItem = Form.Item;
const ButtonGroup = Button.Group;
const Option = Select.Option;

function hasErrors(fieldsError) {
  return Object.keys(fieldsError).some(field => fieldsError[field]);
}

class ReceiveProductForm extends Component {
  static propTypes = {
    form: PropTypes.object.isRequired,
    history: PropTypes.object.isRequired,
    match: PropTypes.object.isRequired,
    loading: PropTypes.bool.isRequired,
    purchaseOrder: PropTypes.object,
    dispensaryId: PropTypes.string.isRequired,
    product: PropTypes.object,
    productStocks: PropTypes.array.isRequired,
    generatedBarcode: PropTypes.string,

    receiveProduct: PropTypes.func.isRequired,
    getPurchaseOrder: PropTypes.func.isRequired,
    getProduct: PropTypes.func.isRequired,
    getProductStocks: PropTypes.func.isRequired,
    generateBarcode: PropTypes.func.isRequired,
  };

  componentDidMount() {
    this._load();
  }

  componentDidUpdate(prevProps) {
    if (this.props.match.params.purchaseId !== prevProps.match.params.purchaseId) this._load();
  }

  _load() {
    const {getProduct, getProductStocks, getPurchaseOrder, match: {params}} = this.props;
    const {productId, purchaseId} = decodeParams(params);
    if (purchaseId === '-') {
      getProduct({productId});
    } else {
      getPurchaseOrder({purchaseId});
    }
    getProductStocks({productId});
  }

  goBack = () => {
    this.props.history.goBack();
  };

  handleGenerateClick = async() => {
    const {dispensaryId, generateBarcode, form: {setFieldsValue}} = this.props;
    await generateBarcode({dispensaryId});
    const {generatedBarcode} = this.props;
    setFieldsValue({newSku: generatedBarcode});
  };

  handleSubmit = (e) => {
    const {purchaseId, productId} = decodeParams(this.props.match.params);
    e.preventDefault();
    this.props.form.validateFields(async(err, values) => {
      if (err) return;
      try {
        const {quantity, netCostPerUnit, sku, newSku, lotNumber} = values;
        await this.props.receiveProduct({
          purchaseId: purchaseId === '-' ? null : purchaseId,
          productId,
          quantity,
          netCostPerUnit,
          sku: newSku || sku,
          lotNumber: (newSku
            ? lotNumber
            : (this.props.productStocks.find((stock) => (stock.sku === sku)) || {}).lotNumber) || null,
        });
        this.goBack();
      } catch (err) {
        notification.error({
          message: 'Failed',
          description: getDescriptionFromError(err)
        });
      }
    });
  };

  render() {
    const {
      form: {getFieldDecorator, getFieldsError, getFieldValue},
      loading, purchaseOrder, product, productStocks,
      match: {params},
    } = this.props;
    const {productId, purchaseId} = decodeParams(params);
    let purchaseOrderFormItem = null, productDetails;
    if (purchaseId === '-') {
      productDetails = product && product.display;
    } else {
      purchaseOrderFormItem = (
        <FormItem
          {...formItemLayout}
          label="Purchase Order"
        >
          {purchaseOrder && `${purchaseOrder.supplierName} - ${purchaseOrder.status} (${purchaseOrder.modifiedAt})`}
        </FormItem>
      );
      const products = (purchaseOrder && purchaseOrder.items) || [];
      const product = products.find(({productId: pid}) => (pid === productId));
      productDetails = product && `${product.quantity} x ${product.productDisplay} @ ${product.netCostPerUnit || 'N/A'}`;
    }
    const isNewSku = getFieldValue('sku') === null;
    return (
      <Card title="Receive Product">
        <Form onSubmit={this.handleSubmit}>
          {purchaseOrderFormItem}
          <FormItem
            {...formItemLayout}
            label="Product"
          >
            {productDetails}
          </FormItem>
          <FormItem
            {...formItemLayout}
            label="Quantity"
          >
            {getFieldDecorator('quantity', {
              rules: [
                {required: true, message: 'Quantity is required'}
              ]
            })(
              <InputNumber />
            )}
          </FormItem>
          <FormItem
            {...formItemLayout}
            label="Net Cost Per Unit"
          >
            {getFieldDecorator('netCostPerUnit', {
              rules: [
                {required: true, message: 'Net cost per unit is required'}
              ]
            })(
              <InputNumber />
            )}
          </FormItem>
          <FormItem
            {...formItemLayout}
            label="Stock"
          >
            {getFieldDecorator('sku', {initialValue: null})(
              <Select>
                <Option key="new-sku" value={null}>--- NEW ---</Option>
                {productStocks.map(stock =>
                  <Option key={stock.sku} value={stock.sku}><StockDisplay stock={stock}/></Option>
                )}
              </Select>
            )}
          </FormItem>
          {isNewSku && [
            <FormItem
              key="sku"
              {...formItemLayout}
              label="SKU"
            >
              <Row gutter={16}>
                <Col span={16}>
                  {getFieldDecorator('newSku', {
                    rules: [{required: true, message: 'SKU is required'}]
                  })(
                    <Input />
                  )}
                </Col>
                <Col span={8}><Button onClick={this.handleGenerateClick}>Generate</Button></Col>
              </Row>
            </FormItem>,
            <FormItem
              key="lotNumber"
              {...formItemLayout}
              label="Lot Number"
            >
              {getFieldDecorator('lotNumber')(
                <Input />
              )}
            </FormItem>
          ]}
          <FormItem>
            <ButtonGroup>
              <Button
                type="primary"
                htmlType="submit"
                disabled={loading || hasErrors(getFieldsError())}
              >
                Receive Product
              </Button>
              <Button
                htmlType="button"
                onClick={this.goBack}
              >
                Cancel
              </Button>
            </ButtonGroup>
          </FormItem>
        </Form>
      </Card>
    );
  }
}

const ReceiveProductPage = Form.create()(ReceiveProductForm);

const mapStateToProps = state => ({
  dispensaryId: state.dispensary.selectedDispensaryId,
  loading: state.purchaseOrder.purchaseOrderLoading,
  purchaseOrder: state.purchaseOrder.purchaseOrder,
  product: state.product.product,
  productStocks: state.product.productStocks,
  generatedBarcode: state.product.generatedBarcode,
});

const mapDispatchToProps = dispatch => (bindActionCreators({
  receiveProduct,
  getPurchaseOrder,
  getProduct,
  getProductStocks,
  generateBarcode,
}, dispatch));

export default connect(mapStateToProps, mapDispatchToProps)(ReceiveProductPage);



// WEBPACK FOOTER //
// ./src/Product/ReceiveProductPage.js